import { useEffect, useMemo, useState } from 'react';

import { Grid, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import dayjs from 'dayjs';

import { useAppDispatch } from '@/store/hooks';
import {
  patientsApiSlice,
  useUpdatePatientOverviewMutation
} from '@/store/slices/api/patientsApiSlice';
import { reportsPatientApiSlice } from '@/store/slices/api/transmissionReport/reportsPatientApiSlice';
import { usStateOptions } from '@/utils/constants';
import { parseCalendarDate } from '@/utils/formatDate';
import {
  type AddressErrors,
  type AddressField,
  hasAnyAddressPart,
  normalizeUsState,
  validateAddress
} from '@/utils/addressValidation';
import { formatUsZip } from '@/utils/numericFields';
import { PATIENT_SEX_OPTIONS, selectablePatientSex } from '@/utils/patientSex';
import { API_SLICE } from '@/utils/constants/apiEndpoints';

import { LabelAndValue } from '@/types/custom';
import DatePickerWrapper from '@/ui/DatePickerWrapper';
import Dropdown from '@/ui/Dropdown';
import LabelText from '@/ui/LabelText';
import CustomModal from '@/ui/Modal';
import TextInputWrapper from '@/ui/TextInputWrapper';

import styles from './PatientOverview.module.scss';
import { formatPhoneInput, hasStoredPhone, PHONE_FIELDS, phoneFormErrors } from './patientPhones';

export interface PatientOverviewDetails {
  id: string;
  firstName?: string;
  lastName?: string;
  preferredName?: string;
  address1?: string;
  sex?: string;
  address2?: string;
  city?: string;
  state?: string;
  zip?: string;
  phoneNumber?: string;
  homePhone?: string;
  otherPhone?: string;
  mrn2?: string;
  birthDate?: string;
  addressId?: string;
}

interface PatientEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  patient: PatientOverviewDetails;
  mrnLabel?: string;
  /** Address must be filled in (enrollment). Otherwise it's optional, but all-or-nothing. */
  requireAddress?: boolean;
}

const ADDRESS_FIELD_MAP: Record<string, AddressField> = {
  address1: 'line1',
  address2: 'line2',
  city: 'city',
  state: 'state',
  zip: 'postalCode'
};

const PatientEditModal = ({
  isOpen,
  onClose,
  patient,
  mrnLabel = 'MRN',
  requireAddress = false
}: PatientEditModalProps) => {
  const [formPatientInfo, setFormPatientInfo] = useState<PatientOverviewDetails>(patient);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [addressTouched, setAddressTouched] = useState<Partial<Record<AddressField, boolean>>>({});
  const [updatePatient] = useUpdatePatientOverviewMutation();

  const dispatch = useAppDispatch();

  const savedMrn = patient.mrn2?.trim() ?? '';
  const mrnAlreadySet = savedMrn !== '' && savedMrn !== '-' && savedMrn !== '—';
  const requiredFields: (keyof PatientOverviewDetails)[] = [
    'firstName',
    'lastName',
    ...(mrnAlreadySet ? (['mrn2'] as const) : []),
    'sex',
    'birthDate'
  ];

  useEffect(() => {
    if (isOpen) {
      setFormPatientInfo({
        ...patient,
        // Anything other than male or female is not set, so the menu shows blank.
        sex: selectablePatientSex(patient.sex),
        zip: formatUsZip(patient.zip ?? ''),
        // Older records may hold a full state name; the picker needs the code.
        state: normalizeUsState(patient.state) || patient.state
      });
      setErrors(phoneFormErrors(patient, { requireOne: hasStoredPhone(patient) }));
      // Show problems with an address that's already saved right away.
      const saved = validateAddress(
        {
          line1: patient.address1,
          line2: patient.address2,
          city: patient.city,
          state: patient.state,
          postalCode: patient.zip
        },
        { required: false }
      );
      setAddressTouched(
        Object.fromEntries(Object.keys(saved).map((field) => [field, true])) as Partial<
          Record<AddressField, boolean>
        >
      );
    }
  }, [isOpen]);

  const validateField = (field: keyof PatientOverviewDetails, value: string, label: string) => {
    let error = '';

    // Required field validation
    if (requiredFields.includes(field) && (!value || value.trim() === '')) {
      error = `${label} is required`;
      setErrors((prev) => ({ ...prev, [field]: error }));
      return;
    }

    // Address fields are validated together in addressErrors (they depend on each other).
    if (ADDRESS_FIELD_MAP[field]) {
      setAddressTouched((prev) => ({ ...prev, [ADDRESS_FIELD_MAP[field]]: true }));
      return;
    }

    if ((PHONE_FIELDS as readonly string[]).includes(field)) {
      setErrors((prev) => {
        const next = { ...prev };
        for (const phoneField of PHONE_FIELDS) delete next[phoneField];
        return {
          ...next,
          ...phoneFormErrors(
            { ...formPatientInfo, [field]: value },
            { requireOne: hasStoredPhone(patient) }
          )
        };
      });
      return;
    }

    setErrors((prev) => ({ ...prev, [field]: error }));
  };

  const handleFieldChange = (field: keyof PatientOverviewDetails, value: string, label: string) => {
    const nextValue = (PHONE_FIELDS as readonly string[]).includes(field)
      ? formatPhoneInput(value)
      : field === 'zip'
        ? formatUsZip(value)
        : value;
    setFormPatientInfo((prev) => ({ ...prev, [field]: nextValue }));
    validateField(field, nextValue, label);
  };

  const addressErrors: AddressErrors = useMemo(
    () =>
      validateAddress(
        {
          line1: formPatientInfo.address1,
          line2: formPatientInfo.address2,
          city: formPatientInfo.city,
          state: formPatientInfo.state,
          postalCode: formPatientInfo.zip
        },
        { required: requireAddress }
      ),
    [
      formPatientInfo.address1,
      formPatientInfo.address2,
      formPatientInfo.city,
      formPatientInfo.state,
      formPatientInfo.zip,
      requireAddress
    ]
  );
  const addressValid = Object.keys(addressErrors).length === 0;

  /** Inline address error, shown once the field has been touched (or on a save attempt). */
  const addressError = (field: keyof PatientOverviewDetails) => {
    const key = ADDRESS_FIELD_MAP[field];
    return key && addressTouched[key] ? addressErrors[key] : undefined;
  };

  const isFormValid = useMemo(() => {
    // Check if all required fields are filled
    const requiredFieldsFilled = requiredFields.every((field) => {
      const value = formPatientInfo[field];
      return value !== undefined && value !== null && value.toString().trim() !== '';
    });

    // Check if there are no validation errors
    const noErrors = Object.values(errors).every((msg) => msg === '');
    const phonesValid =
      Object.keys(phoneFormErrors(formPatientInfo, { requireOne: hasStoredPhone(patient) }))
        .length === 0;

    return requiredFieldsFilled && noErrors && phonesValid && addressValid;
  }, [formPatientInfo, errors, mrnAlreadySet, addressValid, patient]);

  const handleSave = async () => {
    const phoneErrors = phoneFormErrors(formPatientInfo, { requireOne: hasStoredPhone(patient) });
    if (Object.keys(phoneErrors).length > 0) {
      setErrors((prev) => ({ ...prev, ...phoneErrors }));
      return;
    }
    if (!addressValid) {
      setAddressTouched({ line1: true, line2: true, city: true, state: true, postalCode: true });
      return;
    }

    setIsSaving(true);

    const mrn2 = formPatientInfo.mrn2?.trim();
    const payload = {
      familyName: formPatientInfo.lastName || '',
      givenName: formPatientInfo.firstName || '',
      preferredName: formPatientInfo.preferredName || '',
      ...(mrn2 ? { mrn2 } : {}),
      birthDate: formPatientInfo.birthDate || '',
      address: {
        id: formPatientInfo.addressId || undefined,
        line1: (formPatientInfo.address1 || '').trim(),
        line2: (formPatientInfo.address2 || '').trim(),
        city: (formPatientInfo.city || '').trim(),
        state: normalizeUsState(formPatientInfo.state),
        postalCode: (formPatientInfo.zip || '').trim()
      },
      phoneNumber: formPatientInfo.phoneNumber || '',
      homePhone: formPatientInfo.homePhone || '',
      otherPhone: formPatientInfo.otherPhone || '',
      sex: formPatientInfo.sex || ''
    };

    try {
      await updatePatient({ id: patient.id, body: payload }).unwrap();

      dispatch(
        patientsApiSlice.util.updateQueryData('getPatient', patient.id, (draft) => {
          draft.givenName = payload.givenName;
          draft.familyName = payload.familyName;
          draft.preferredName = payload.preferredName;
          if (payload.mrn2) draft.mrn2 = payload.mrn2;
          draft.birthDate = payload.birthDate;
          draft.sex = payload.sex;
          draft.phoneNumber = payload.phoneNumber;
          draft.homePhone = payload.homePhone;
          draft.otherPhone = payload.otherPhone;
          draft.address = {
            ...(draft.address ?? {}),
            id: payload.address.id ?? draft.address?.id ?? '',
            line1: payload.address.line1,
            line2: payload.address.line2,
            city: payload.address.city,
            state: payload.address.state,
            postalCode: payload.address.postalCode
          } as typeof draft.address;
        })
      );
      dispatch(
        reportsPatientApiSlice.util.updateQueryData('getReportPatientDetails', patient.id, (draft) => {
          draft.patient.givenName = payload.givenName;
          draft.patient.familyName = payload.familyName;
          if (payload.mrn2) draft.patient.mrn2 = payload.mrn2;
          draft.patient.birthDate = payload.birthDate;
          draft.patient.sex = payload.sex;
        })
      );

      dispatch(patientsApiSlice.util.invalidateTags([{ type: API_SLICE.Patient, id: patient.id }]));
      dispatch(
        reportsPatientApiSlice.util.invalidateTags([{ type: 'ReportPatient', id: patient.id }])
      );

      notifications.show({
        title: 'Success',
        message: 'Patient information updated successfully.',
        color: 'green'
      });

      onClose();
    } catch (error) {
      console.error('Failed to update patient:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to update patient information. Please try again.',
        color: 'red'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const renderTextInput = (
    label: string,
    field: keyof PatientOverviewDetails,
    required: boolean = false
  ) => (
    <TextInputWrapper
      className={styles.inputText}
      label={label}
      value={formPatientInfo[field] || ''}
      onChange={(e) => handleFieldChange(field, e.target.value, label)}
      required={required}
      error={errors[field] || addressError(field)}
    />
  );

  const renderSelect = (
    label: string,
    options: LabelAndValue[],
    field: keyof PatientOverviewDetails,
    value: string | undefined,
    required: boolean = false
  ) => {
    const selected = options.find((opt) => opt.value === value);
    return (
      <Dropdown
        label={label}
        options={options}
        value={selected}
        onChange={(option) => handleFieldChange(field, (option as LabelAndValue).value, label)}
        placeholder={`Select ${label}`}
        labelTextProps={{ variant: 'regular' }}
        required={required}
      />
    );
  };

  const addressRequired =
    requireAddress ||
    hasAnyAddressPart({
      line1: formPatientInfo.address1,
      line2: formPatientInfo.address2,
      city: formPatientInfo.city,
      state: formPatientInfo.state,
      postalCode: formPatientInfo.zip
    });

  return (
    <CustomModal
      modalProps={{
        opened: isOpen,
        onClose,
        size: 'xl',
        closeOnClickOutside: false,
        closeOnEscape: false
      }}
      header={{ title: 'Edit Patient Overview' }}
      primaryButton={{
        text: 'Save Changes',
        isLoading: isSaving,
        onClick: handleSave,
        isDisabled: !isFormValid || isSaving
      }}
      secondaryButton={{
        text: 'Cancel',
        isDisabled: isSaving,
        onClick: onClose
      }}>
      <div className={styles.patientSection}>
        <Grid mt="md">
          <Grid.Col span={{ base: 12, sm: 3 }}>
            {renderTextInput('First Name', 'firstName', true)}
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 3 }}>
            {renderTextInput('Last Name', 'lastName', true)}
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 3 }}>
            {renderTextInput('Middle Name', 'preferredName')}
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 3 }}>
            {renderTextInput(mrnLabel, 'mrn2', mrnAlreadySet)}
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 2 }}>
            {renderSelect('Sex', PATIENT_SEX_OPTIONS, 'sex', formPatientInfo.sex, true)}
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 3 }}>
            <LabelText label="Date of Birth" required variant="regular" boxProps={{ mb: 4 }} />
            <DatePickerWrapper
              inputProps={{ placeholder: 'MM/DD/YYYY', required: true }}
              datePickerProps={{
                id: 'dob',
                selected: parseCalendarDate(formPatientInfo.birthDate),
                onChange: (date: Date | null) =>
                  handleFieldChange(
                    'birthDate',
                    date ? dayjs(date).format('YYYY-MM-DD') : '',
                    'Date of Birth'
                  ),
                required: true,
                isClearable: false
              }}
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 4 }}>{renderTextInput('Address 1', 'address1', addressRequired)}</Grid.Col>
          <Grid.Col span={{ base: 12, sm: 3 }}>{renderTextInput('Address 2', 'address2')}</Grid.Col>

          <Grid.Col span={{ base: 12, sm: 4 }}>{renderTextInput('City', 'city', addressRequired)}</Grid.Col>
          <Grid.Col span={{ base: 12, sm: 4 }}>
            {renderSelect('State', usStateOptions, 'state', formPatientInfo.state, addressRequired)}
            {addressError('state') && (
              <Text size="xs" c="red" mt={4} role="alert">
                {addressError('state')}
              </Text>
            )}
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 4 }}>
            <TextInputWrapper
              className={styles.inputText}
              label="Zip"
              value={formPatientInfo.zip || ''}
              inputMode="numeric"
              onChange={(e) => handleFieldChange('zip', e.target.value, 'Zip')}
              required={addressRequired}
              error={addressError('zip')}
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 4 }}>
            {renderTextInput('Mobile Phone', 'phoneNumber')}
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 4 }}>
            {renderTextInput('Home Phone', 'homePhone')}
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 4 }}>
            {renderTextInput('Other Phone', 'otherPhone')}
          </Grid.Col>
        </Grid>
      </div>
    </CustomModal>
  );
};

export default PatientEditModal;
